import { useFlowRepo } from "app/features/stores/mod.ts";
import { useLocationHref } from "app/hooks/use_location_href.ts";
import { navigateTo, useRouter } from "app/hooks/use_router.ts";
import { MathPlus } from "icons/math_plus.tsx";
import { PlayList } from "icons/play_list.tsx";
import { Trash } from "icons/trash.tsx";
import { nanoid } from "local/deps/nanoid.ts";
import { useSignal } from "local/deps/preact/signals.ts";
import { Icon } from "local/ui/icon.tsx";
import { Item } from "local/ui/item.tsx";
import { List } from "local/ui/list.tsx";
import { Panel } from "local/ui/panel.tsx";
import { Spinner } from "local/ui/spinner.tsx";
import { Tag } from "local/ui/tag.tsx";
import { View } from "local/ui/view.tsx";

/** MAIN **/

export function Flows() {
  const repo = useFlowRepo();
  const loading = repo.store.value.pending > 0;

  const router = useRouter();

  const creating = useSignal(false);

  const href = useLocationHref();

  async function onTrash(id: string) {
    const confirmed = window.confirm("Delete this flow?");
    if (confirmed) {
      const match = router.match(href);
      if (match?.data?.flowId === id) {
        navigateTo(router.url("flow"));
      }
      await repo.remove(id);
    }
  }

  async function onCreate() {
    creating.value = true;
    const data = await repo.create({
      id: nanoid(),
      name: "New Flow",
      steps: [],
    });
    creating.value = false;
    navigateTo(router.url("flow", data.id));
  }

  const { data, pending } = repo.store.value;

  // NOTE: Sort on copy.
  const items = data.slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Panel
      gradient
      header={
        <View tag="h2" class="flex gap-2 items-center text-2xl">
          Flows
          {!loading && data.length > 0 && (
            <span>
              <Tag class="mr-3">{data.length}</Tag>
            </span>
          )}
        </View>
      }
    >
      <List>
        <Item
          interactive
          color="green"
          onClick={onCreate}
          disabled={creating.value}
        >
          {!creating.value && (
            <Icon>
              <MathPlus />
            </Icon>
          )}
          {creating.value && <Spinner />}
          Create Flow
        </Item>
        {loading && (
          <Item>
            <Spinner />
            <p class="text-gray-600 dark:text-gray-400 text-xs italic">
              Loading your flows...
            </p>
          </Item>
        )}
        {items.map((item) => {
          const itemHref = router.href("flow", item.id);
          return (
            <Item
              id={item.id}
              key={item.id}
              title={item.name}
              onSecondaryAction={() => onTrash(item.id)}
              selected={itemHref === href}
              interactive
              href={itemHref}
              icon={<PlayList />}
              secondaryIcon={<Trash />}
              placeholder="Untitled"
            />
          );
        })}
      </List>
    </Panel>
  );
}
